import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  InputNumber,
  Space,
  Spin,
  Table,
  Tag,
  Tabs,
  Typography,
  DatePicker,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useAuthStore } from '@/store/authStore';
import {
  getSalesDashboardActionSummary,
  getSalesDashboardCrmFollowUps,
  getSalesDashboardPendingApprovals,
  getSalesDashboardOrdersAtRisk,
  getSalesDashboardQuotationConversionQueues,
  getSalesDashboardAttentionBrief,
  getSalesDashboardOutlook,
  type SalesDashboardActionSummary,
  type CustomerOutstandingDetailRow,
  type SalesOrderPendingWorkOrderRow,
} from '@/services/salesDashboard.service';
import { getSalesControlTowerLayout, getRefreshIntervalMs } from './layoutProfile';
import { ControlTowerSectionCard } from '@/components/control-tower/ControlTowerSectionCard';
import { EmptyQueueState } from './EmptyQueueState';
import { DeliveryQueuesCard } from './DeliveryQueuesCard';
import { MyTasksZone } from './MyTasksZone';
import { ApprovalStrip } from './ApprovalStrip';
import { CrmFollowUpZone } from './CrmFollowUpZone';
import { QuotationConversionZone } from './QuotationConversionZone';
import { SalesAiBriefStrip } from './SalesAiBriefStrip';
import { SalesOutlookRibbon } from './SalesOutlookRibbon';
import { SalesAnalyticsWorkspace } from '@/components/sales/analytics/SalesAnalyticsWorkspace';
import { parseAnalyticsFiltersFromSearchParams } from '@/services/salesAnalytics.service';

const { Title, Text, Paragraph } = Typography;

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

type OpsQueueTab = 'cash' | 'orders' | 'commercial' | 'crm' | 'fulfilment' | 'mywork';

export const SalesControlTowerPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = theme.useToken();
  const { formatCurrency } = useCurrency();
  const user = useAuthStore((s) => s.user);
  const layoutUser = user as Parameters<typeof getSalesControlTowerLayout>[0];
  const { profile, visibility } = getSalesControlTowerLayout(layoutUser);
  const refreshMs = getRefreshIntervalMs(layoutUser);

  const asOnDate = searchParams.get('as_on_date') || undefined;
  const dueSoonDays = Number(searchParams.get('due_soon_days') || '7') || 7;
  const towerTab = searchParams.get('tab') === 'analytics' ? 'analytics' : 'operations';
  const analyticsFiltersSummary = useMemo(() => {
    const f = parseAnalyticsFiltersFromSearchParams(searchParams);
    return [
      `${f.date_from} → ${f.date_to}`,
      f.sales_team_id ? 'Team filtered' : null,
      f.customer_id ? 'Customer filtered' : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }, [searchParams]);

  const setTowerTab = (tab: 'operations' | 'analytics') => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [arAlert, setArAlert] = useState<string | null>(null);
  const [opsQueueTab, setOpsQueueTab] = useState<OpsQueueTab>('cash');
  const [arPageSize, setArPageSize] = useState(10);
  const [arPage, setArPage] = useState(1);

  const defaultOpsQueueTab = useMemo((): OpsQueueTab => {
    if (visibility.cashQueues) return 'cash';
    if (visibility.quotationConversion) return 'commercial';
    if (visibility.ordersAtRisk || visibility.pendingWorkOrder) return 'orders';
    if (visibility.deliveries) return 'fulfilment';
    if (visibility.crmFollowUps) return 'crm';
    return 'mywork';
  }, [
    profile,
    visibility.cashQueues,
    visibility.quotationConversion,
    visibility.ordersAtRisk,
    visibility.pendingWorkOrder,
    visibility.deliveries,
    visibility.crmFollowUps,
  ]);

  const summaryQuery = useQuery({
    queryKey: ['control-tower', 'action-summary', asOnDate, dueSoonDays],
    queryFn: () =>
      getSalesDashboardActionSummary({
        as_on_date: asOnDate,
        due_soon_days: dueSoonDays,
      }),
    refetchInterval: refreshMs || false,
  });

  const briefQuery = useQuery({
    queryKey: ['control-tower', 'attention-brief', asOnDate, dueSoonDays],
    queryFn: () =>
      getSalesDashboardAttentionBrief({
        as_on_date: asOnDate,
        due_soon_days: dueSoonDays,
      }),
    enabled: visibility.attentionBrief,
    refetchInterval: refreshMs || false,
  });

  const outlookQuery = useQuery({
    queryKey: ['control-tower', 'outlook', asOnDate],
    queryFn: () => getSalesDashboardOutlook({ as_on_date: asOnDate }),
    enabled: visibility.outlook,
    refetchInterval: refreshMs || false,
  });

  const crmQuery = useQuery({
    queryKey: ['control-tower', 'crm-followups'],
    queryFn: getSalesDashboardCrmFollowUps,
    enabled: visibility.crmFollowUps,
    refetchInterval: refreshMs || false,
  });

  const quotationConversionQuery = useQuery({
    queryKey: ['control-tower', 'quotation-conversion'],
    queryFn: () => getSalesDashboardQuotationConversionQueues(),
    enabled: visibility.quotationConversion,
    refetchInterval: refreshMs || false,
  });

  const approvalsQuery = useQuery({
    queryKey: ['control-tower', 'pending-approvals'],
    queryFn: getSalesDashboardPendingApprovals,
    enabled: visibility.approvals,
    refetchInterval: refreshMs || false,
  });

  const atRiskQuery = useQuery({
    queryKey: ['control-tower', 'orders-at-risk'],
    queryFn: getSalesDashboardOrdersAtRisk,
    enabled: visibility.ordersAtRisk,
    refetchInterval: refreshMs || false,
  });

  const s: SalesDashboardActionSummary = summaryQuery.data ?? {
    overdue_customer_invoices: [],
    due_today_customer_invoices: [],
    due_soon_customer_invoices: [],
    ar_overdue_total: 0,
    ar_due_today_total: 0,
    ar_due_soon_total: 0,
    overdue_ar_by_customer: [],
    due_soon_days: 7,
    pending_review_sales_orders: [],
    orders_in_progress: [],
    sales_orders_pending_work_order: [],
    deliveries_not_invoiced: [],
    delivery_queues: {
      delivered_not_invoiced: [],
      partially_invoiced: [],
      in_transit: [],
      doc_package_pending: [],
    },
    draft_sales_order_count: 0,
    draft_sales_invoice_count: 0,
  };

  useEffect(() => {
    const section = searchParams.get('section');
    if (towerTab !== 'operations' || !section) return;
    if (section.startsWith('cash')) setOpsQueueTab('cash');
    else if (section === 'orders-at-risk' || section === 'pending-work-order') setOpsQueueTab('orders');
    else if (section === 'quotation-conversion') setOpsQueueTab('commercial');
    else if (section === 'crm-followups') setOpsQueueTab('crm');
    else if (section === 'delivery-queues') setOpsQueueTab('fulfilment');
    else if (section === 'my-work' || section === 'attention-brief') setOpsQueueTab('mywork');
    const t = window.setTimeout(() => scrollToSection(section), 80);
    return () => window.clearTimeout(t);
  }, [towerTab, searchParams]);

  useEffect(() => {
    if (summaryQuery.isSuccess) {
      const storageKey = 'sales_tower_overdue_count';
      const prevRaw = sessionStorage.getItem(storageKey);
      const prev = prevRaw != null ? Number(prevRaw) : null;
      const cur = s.overdue_customer_invoices.length;
      if (prev != null && cur > prev) {
        setArAlert(`${cur - prev} new overdue AR item(s) since last visit.`);
      }
      sessionStorage.setItem(storageKey, String(cur));
      setLastUpdated(new Date());
    }
  }, [summaryQuery.isSuccess, s.overdue_customer_invoices.length]);

  // Only reset queue tab when layout profile changes — not on every render
  // (visibility is a new object identity each call to getSalesControlTowerLayout).
  useEffect(() => {
    setOpsQueueTab(defaultOpsQueueTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- profile-driven default only
  }, [profile]);

  const refetchAll = () => {
    summaryQuery.refetch();
    briefQuery.refetch();
    outlookQuery.refetch();
    crmQuery.refetch();
    quotationConversionQuery.refetch();
    approvalsQuery.refetch();
    atRiskQuery.refetch();
  };

  const loadingInitial = summaryQuery.isLoading && !summaryQuery.data;

  const arInvoiceColumns: ColumnsType<CustomerOutstandingDetailRow> = [
    {
      title: 'Invoice',
      dataIndex: 'invoice_number',
      width: 150,
      ellipsis: true,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v}</span>,
    },
    { title: 'Customer', dataIndex: 'customer_name', ellipsis: true },
    {
      title: 'Due',
      dataIndex: 'due_date',
      width: 110,
      render: (v: string) => <span style={{ whiteSpace: 'nowrap' }}>{v || '—'}</span>,
    },
    {
      title: 'Overdue',
      dataIndex: 'overdue_days',
      width: 90,
      render: (d: number) => (d > 0 ? <Tag color="red">{d}d</Tag> : '—'),
    },
    {
      title: 'Balance',
      dataIndex: 'balance_due',
      align: 'right',
      width: 140,
      render: (v: number) => (
        <span style={{ whiteSpace: 'nowrap' }}>{formatCurrency(v ?? 0)}</span>
      ),
    },
    {
      title: 'Actions',
      key: 'action',
      width: 170,
      fixed: 'right',
      render: (_: unknown, r) => (
        <Space size={0} wrap={false}>
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/fin/reports/customer-statement?customer_id=${r.customer_id}`)}
          >
            Statement
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/fin/receipt-vouchers/new?customer_id=${r.customer_id}`)}
          >
            Receipt
          </Button>
        </Space>
      ),
    },
  ];

  const arPagination = {
    current: arPage,
    pageSize: arPageSize,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50'],
    onChange: (page: number, size: number) => {
      setArPage(page);
      if (size !== arPageSize) {
        setArPageSize(size);
        setArPage(1);
      }
    },
  };

  const pendingWorkOrderColumns: ColumnsType<SalesOrderPendingWorkOrderRow> = [
    { title: 'SO #', dataIndex: 'sales_order_number', width: 120 },
    { title: 'Customer', dataIndex: 'customer_name', ellipsis: true },
    {
      title: 'Days waiting',
      dataIndex: 'days_since_approved',
      width: 100,
      render: (d: number) => (d > 0 ? <Tag color="orange">{d}d</Tag> : '—'),
    },
    { title: 'Required', dataIndex: 'required_date', width: 100 },
    {
      title: '',
      key: 'actions',
      width: 140,
      render: (_: unknown, r) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => navigate(`/growth/orders/${r.id}`)}>
            Open
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/mfg/work-orders/new?sales_order_id=${r.id}`)}
          >
            Create WO
          </Button>
        </Space>
      ),
    },
  ];

  const cashCount =
    s.overdue_customer_invoices.length +
    s.due_today_customer_invoices.length +
    s.due_soon_customer_invoices.length;
  const ordersCount =
    (atRiskQuery.data?.length ?? 0) + s.sales_orders_pending_work_order.length;
  const commercialCount = quotationConversionQuery.data?.kpis.open_commercial_risk_count ?? 0;
  const crmCount =
    (crmQuery.data?.stale_opportunities.length ?? 0) +
    (crmQuery.data?.overdue_activities.length ?? 0) +
    (crmQuery.data?.quotations_expiring.length ?? 0) +
    (crmQuery.data?.missing_customer_po.length ?? 0);
  const fulfilmentCount =
    s.delivery_queues.delivered_not_invoiced.length +
    s.delivery_queues.partially_invoiced.length +
    s.delivery_queues.in_transit.length +
    s.delivery_queues.doc_package_pending.length;

  const queueTabItems = useMemo(() => {
    const items: { key: OpsQueueTab; label: string; children: React.ReactNode }[] = [];

    if (visibility.cashQueues) {
      items.push({
        key: 'cash',
        label: `Cash (${cashCount})`,
        children: (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <ControlTowerSectionCard
              zone="overdue"
              sectionId="cash-overdue"
              title={
                <Space>
                  <WarningOutlined style={{ color: token.colorError }} />
                  <span>Overdue AR ({s.overdue_customer_invoices.length})</span>
                </Space>
              }
            >
              {s.overdue_customer_invoices.length === 0 ? (
                <EmptyQueueState
                  message="All AR collections are current."
                  actionLabel="Outstanding report"
                  onAction={() => navigate('/fin/reports/customer-outstanding')}
                />
              ) : (
                <Table
                  rowKey="invoice_id"
                  size="small"
                  tableLayout="fixed"
                  scroll={{ x: 900 }}
                  pagination={arPagination}
                  dataSource={s.overdue_customer_invoices}
                  columns={arInvoiceColumns}
                />
              )}
            </ControlTowerSectionCard>

            <ControlTowerSectionCard
              zone="dueToday"
              sectionId="cash-due-today"
              title={`Due today (${s.due_today_customer_invoices.length}) · ${formatCurrency(s.ar_due_today_total)}`}
            >
              {s.due_today_customer_invoices.length === 0 ? (
                <EmptyQueueState message="Nothing due today." />
              ) : (
                <Table
                  rowKey="invoice_id"
                  size="small"
                  tableLayout="fixed"
                  scroll={{ x: 900 }}
                  pagination={arPagination}
                  dataSource={s.due_today_customer_invoices}
                  columns={arInvoiceColumns}
                />
              )}
            </ControlTowerSectionCard>

            <ControlTowerSectionCard
              zone="dueSoon"
              sectionId="cash-due-soon"
              title={`Due soon (${s.due_soon_days}d) · ${s.due_soon_customer_invoices.length} · ${formatCurrency(s.ar_due_soon_total)}`}
            >
              {s.due_soon_customer_invoices.length === 0 ? (
                <EmptyQueueState message="Nothing due in the selected window." />
              ) : (
                <Table
                  rowKey="invoice_id"
                  size="small"
                  tableLayout="fixed"
                  scroll={{ x: 900 }}
                  pagination={arPagination}
                  dataSource={s.due_soon_customer_invoices}
                  columns={arInvoiceColumns}
                />
              )}
            </ControlTowerSectionCard>

            {s.overdue_ar_by_customer.length > 0 && (
              <ControlTowerSectionCard zone="collectionsDetail" title="Top overdue customers">
                <Table
                  dataSource={s.overdue_ar_by_customer}
                  rowKey="customer_id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: 'Customer', dataIndex: 'customer_name' },
                    {
                      title: 'Balance',
                      dataIndex: 'balance_due',
                      align: 'right',
                      render: (v: number) => formatCurrency(v),
                    },
                    { title: 'Invoices', dataIndex: 'invoice_count', width: 80 },
                    { title: 'Max days', dataIndex: 'max_overdue_days', width: 90 },
                    {
                      title: '',
                      key: 'actions',
                      render: (_: unknown, r) => (
                        <Button
                          type="link"
                          size="small"
                          onClick={() =>
                            navigate(`/fin/receipt-vouchers/new?customer_id=${r.customer_id}`)
                          }
                        >
                          Receipt
                        </Button>
                      ),
                    },
                  ]}
                />
              </ControlTowerSectionCard>
            )}
          </Space>
        ),
      });
    }

    if (visibility.ordersAtRisk || visibility.pendingWorkOrder) {
      items.push({
        key: 'orders',
        label: `Orders (${ordersCount})`,
        children: (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {visibility.ordersAtRisk && (
              <ControlTowerSectionCard zone="ordersAtRisk" sectionId="orders-at-risk" title="Orders at risk">
                {atRiskQuery.isLoading ? (
                  <Spin />
                ) : atRiskQuery.isError ? (
                  <Alert type="warning" message={(atRiskQuery.error as Error).message} />
                ) : !atRiskQuery.data?.length ? (
                  <EmptyQueueState message="No orders at risk in the next 14 days." />
                ) : (
                  <Table
                    rowKey="sales_order_id"
                    size="small"
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    dataSource={atRiskQuery.data}
                    columns={[
                      { title: 'SO #', dataIndex: 'sales_order_number', width: 120 },
                      { title: 'Customer', dataIndex: 'customer_name', ellipsis: true },
                      { title: 'Required', dataIndex: 'required_date', width: 100 },
                      { title: 'WO', dataIndex: 'work_order_number', width: 100 },
                      { title: 'Reason', dataIndex: 'blocking_reason', ellipsis: true },
                      {
                        title: '',
                        key: 'action',
                        render: (_: unknown, r) => (
                          <Button
                            type="link"
                            size="small"
                            onClick={() => navigate(`/growth/orders/${r.sales_order_id}/status-check`)}
                          >
                            Health
                          </Button>
                        ),
                      },
                    ]}
                  />
                )}
              </ControlTowerSectionCard>
            )}
            {visibility.pendingWorkOrder && (
              <ControlTowerSectionCard
                zone="pendingWorkOrder"
                sectionId="pending-work-order"
                title="Pending work order conversion"
              >
                {s.sales_orders_pending_work_order.length === 0 ? (
                  <EmptyQueueState message="All approved manufacturing orders have work orders." />
                ) : (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    dataSource={s.sales_orders_pending_work_order}
                    columns={pendingWorkOrderColumns}
                  />
                )}
              </ControlTowerSectionCard>
            )}
            <Button type="link" onClick={() => navigate('/growth/orders')}>
              Open customer order register →
            </Button>
          </Space>
        ),
      });
    }

    if (visibility.quotationConversion) {
      items.push({
        key: 'commercial',
        label: `Commercial (${commercialCount})`,
        children: (
          <div id="quotation-conversion-wrap">
            <QuotationConversionZone
              data={quotationConversionQuery.data}
              loading={quotationConversionQuery.isLoading}
              error={
                quotationConversionQuery.isError
                  ? (quotationConversionQuery.error as Error).message
                  : null
              }
              formatCurrency={formatCurrency}
            />
          </div>
        ),
      });
    }

    if (visibility.crmFollowUps) {
      items.push({
        key: 'crm',
        label: `CRM (${crmCount})`,
        children: (
          <CrmFollowUpZone
            data={crmQuery.data}
            loading={crmQuery.isLoading}
            error={crmQuery.isError ? (crmQuery.error as Error).message : null}
          />
        ),
      });
    }

    if (visibility.deliveries) {
      items.push({
        key: 'fulfilment',
        label: `Fulfilment (${fulfilmentCount})`,
        children: <DeliveryQueuesCard queues={s.delivery_queues} />,
      });
    }

    if (visibility.myTasks || visibility.approvals) {
      items.push({
        key: 'mywork',
        label: `My work (${approvalsQuery.data?.length ?? 0})`,
        children: (
          <Space direction="vertical" size="middle" style={{ width: '100%' }} id="my-work">
            {visibility.approvals && (
              <ApprovalStrip
                rows={approvalsQuery.data ?? []}
                loading={approvalsQuery.isLoading}
                error={approvalsQuery.isError ? (approvalsQuery.error as Error).message : null}
              />
            )}
            {visibility.myTasks && <MyTasksZone />}
          </Space>
        ),
      });
    }

    return items;
    // Columns are stable enough for this page; avoid depending on inline column arrays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    visibility,
    cashCount,
    ordersCount,
    commercialCount,
    crmCount,
    fulfilmentCount,
    s.overdue_customer_invoices,
    s.due_today_customer_invoices,
    s.due_soon_customer_invoices,
    s.overdue_ar_by_customer,
    s.sales_orders_pending_work_order,
    s.delivery_queues,
    s.ar_due_today_total,
    s.ar_due_soon_total,
    s.due_soon_days,
    atRiskQuery.data,
    atRiskQuery.isLoading,
    atRiskQuery.isError,
    quotationConversionQuery.data,
    quotationConversionQuery.isLoading,
    quotationConversionQuery.isError,
    crmQuery.data,
    crmQuery.isLoading,
    crmQuery.isError,
    approvalsQuery.data,
    approvalsQuery.isLoading,
    approvalsQuery.isError,
    formatCurrency,
  ]);

  if (loadingInitial && towerTab === 'operations') {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const operationsContent = (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {arAlert && <Alert type="error" message={arAlert} closable onClose={() => setArAlert(null)} />}
      {summaryQuery.isError && (
        <Alert type="error" message={(summaryQuery.error as Error)?.message ?? 'Action summary failed'} />
      )}

      {visibility.attentionBrief && (
        <SalesAiBriefStrip
          data={briefQuery.data}
          loading={briefQuery.isLoading}
          error={briefQuery.isError ? (briefQuery.error as Error).message : null}
          formatCurrency={formatCurrency}
          onSectionNavigate={(section) => {
            if (section.startsWith('cash')) setOpsQueueTab('cash');
            else if (section === 'orders-at-risk' || section === 'pending-work-order') setOpsQueueTab('orders');
            else if (section === 'quotation-conversion') setOpsQueueTab('commercial');
            else if (section === 'crm-followups') setOpsQueueTab('crm');
            else if (section === 'delivery-queues') setOpsQueueTab('fulfilment');
            else if (section === 'my-work') setOpsQueueTab('mywork');
            window.setTimeout(() => scrollToSection(section), 50);
          }}
        />
      )}

      {visibility.outlook && (
        <SalesOutlookRibbon
          data={outlookQuery.data}
          loading={outlookQuery.isLoading}
          error={outlookQuery.isError ? (outlookQuery.error as Error).message : null}
          formatCurrency={formatCurrency}
        />
      )}

      <ControlTowerSectionCard zone="neutral" sectionId="urgent-queues" title="Urgent queues">
        {queueTabItems.length === 0 ? (
          <EmptyQueueState message="No queues enabled for your layout profile." />
        ) : (
          <Tabs
            size="small"
            activeKey={
              queueTabItems.some((i) => i.key === opsQueueTab)
                ? opsQueueTab
                : queueTabItems[0].key
            }
            destroyInactiveTabPane={false}
            onChange={(k) => {
              setOpsQueueTab(k as OpsQueueTab);
              setArPage(1);
            }}
            items={queueTabItems}
          />
        )}
      </ControlTowerSectionCard>
    </Space>
  );

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: 16,
            position: 'sticky',
            top: 0,
            zIndex: 5,
            background: token.colorBgContainer,
            paddingBottom: 8,
            pointerEvents: 'auto',
          }}
        >
          <div>
            <Title level={3} style={{ marginBottom: 0 }}>
              Growth & Revenue Control Tower
            </Title>
            <Paragraph type="secondary" style={{ marginBottom: 4 }}>
              {towerTab === 'analytics'
                ? 'Advanced sales analytics: funnel, revenue, AR, collections, and team performance.'
                : 'Attention-first operations: brief, outlook, and urgent action queues only.'}
            </Paragraph>
            {towerTab === 'analytics' && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                Active filters: {analyticsFiltersSummary}
                {refreshMs ? ` · auto-refresh ${refreshMs / 60000}m` : ''}
              </Text>
            )}
            {towerTab === 'operations' && (
              <Space wrap size={[4, 4]} style={{ marginBottom: 4 }}>
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('attention-brief')}>
                  Brief
                </Button>
                <Text type="secondary">·</Text>
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('outlook')}>
                  Outlook
                </Button>
                <Text type="secondary">·</Text>
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => scrollToSection('urgent-queues')}>
                  Queues
                </Button>
                <Text type="secondary">·</Text>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  onClick={() => setTowerTab('analytics')}
                >
                  Analytics
                </Button>
              </Space>
            )}
          </div>
          <Space wrap>
            <DatePicker
              value={asOnDate ? dayjs(asOnDate) : null}
              onChange={(d) => {
                const next = new URLSearchParams(searchParams);
                if (d) next.set('as_on_date', d.format('YYYY-MM-DD'));
                else next.delete('as_on_date');
                setSearchParams(next, { replace: true });
              }}
              allowClear
              placeholder="As-on date"
            />
            {visibility.cashQueues && (
              <Space>
                <Text type="secondary">Due soon</Text>
                <InputNumber
                  min={1}
                  max={90}
                  value={dueSoonDays}
                  onChange={(v) => {
                    const next = new URLSearchParams(searchParams);
                    next.set('due_soon_days', String(v || 7));
                    setSearchParams(next, { replace: true });
                  }}
                />
              </Space>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              Updated {lastUpdated.toLocaleTimeString()}
            </Text>
            <Button icon={<ReloadOutlined />} onClick={refetchAll}>
              Refresh
            </Button>
          </Space>
        </div>

        <Tabs
          activeKey={towerTab}
          onChange={(k) => setTowerTab(k === 'analytics' ? 'analytics' : 'operations')}
          items={[
            { key: 'operations', label: 'Operations', children: operationsContent },
            {
              key: 'analytics',
              label: 'Analytics',
              children: <SalesAnalyticsWorkspace />,
            },
          ]}
        />
      </Space>
    </div>
  );
};

export default SalesControlTowerPage;
